/**
 * Gamification visual tokens — aligned with seller dashboard:
 * - Slate neutrals (cards, borders) like stats + orders
 * - Purple → indigo brand gradient (Design library CTA, sidebar focus)
 * - Emerald for money/success (wallet CTA, completed goals)
 * - Amber for urgency nudges
 */

export const GAMIFICATION_CARD = {
  base: 'rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/[0.04]',
  identity:
    'rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50/90 via-white to-white shadow-sm ring-1 ring-slate-900/[0.04]',
  elevated:
    'rounded-2xl border border-purple-200/50 bg-gradient-to-br from-purple-50/35 via-white to-indigo-50/25 shadow-sm ring-1 ring-purple-900/[0.04]',
  momentum:
    'rounded-2xl border border-slate-200/80 bg-gradient-to-r from-white via-white to-purple-50/30 shadow-sm ring-1 ring-slate-900/[0.04]',
  compact:
    'rounded-xl border border-slate-200/80 bg-white/95 shadow-sm ring-1 ring-slate-900/[0.03]',
} as const;

export const GAMIFICATION_TEXT = {
  eyebrow: 'text-[11px] font-semibold uppercase tracking-[0.08em] text-purple-700',
  title: 'text-sm font-semibold tracking-tight text-slate-900',
  body: 'text-sm leading-relaxed text-slate-600',
  footnote: 'text-xs leading-relaxed text-slate-400',
  momentum: 'text-sm font-medium text-purple-800',
  link: 'text-sm font-semibold text-purple-700 hover:text-purple-900',
  linkCompact: 'text-xs font-semibold text-purple-700 hover:text-purple-900',
} as const;

export const GAMIFICATION_PROGRESS = {
  track: 'bg-slate-100',
  fill: 'bg-slate-800',
  fillNear: 'bg-gradient-to-r from-purple-600 to-indigo-600',
  fillComplete: 'bg-emerald-500',
} as const;

export const GAMIFICATION_RING = {
  track: '#f1f5f9',
  gradientStart: '#9333ea',
  gradientEnd: '#4f46e5',
} as const;

export const GAMIFICATION_CHIP = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200/80',
  success: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
  warning: 'bg-amber-50 text-amber-900 ring-amber-200/80',
  brand: 'bg-purple-50 text-purple-900 ring-purple-200/80',
  streak: 'bg-orange-50 text-orange-900 ring-orange-200/80',
} as const;

export const GAMIFICATION_FOCUS_ACCENT = {
  high: 'border-l-amber-500',
  normal: 'border-l-purple-500',
  low: 'border-l-slate-300',
} as const;

export const GAMIFICATION_ACHIEVEMENT = {
  unlocked:
    'bg-gradient-to-br from-purple-50 to-indigo-50 text-purple-700 ring-1 ring-inset ring-purple-200/60 shadow-sm',
  locked: 'border border-dashed border-slate-200 bg-slate-50/50 text-slate-300',
} as const;

export const GAMIFICATION_METRIC =
  'rounded-xl border border-slate-100 bg-gradient-to-br from-slate-50/80 to-white px-3.5 py-3';

export const GAMIFICATION_CTA =
  'inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800';
